import { APIClientServices } from "./APIClientServices";
import { PublicMcpDTO, SeniorityLevel } from "./McpLibraryServices";

// --- DTOs ---

export interface PublicContractedMcpDTO {
	id: string;
	mcpId: string;
	teamMemberId: string;
	userId: string;
	config: Record<string, any>;
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
	mcp?: PublicMcpDTO;
}

export interface HireMemberPayload {
	mcp_id: number;
	name?: string;
	seniority?: SeniorityLevel;
	config?: Record<string, any>;
}

export interface UpdateMemberPayload {
	name?: string;
	seniority?: SeniorityLevel;
	custom_system_prompt?: string | null;
	model_provider?: string;
	model_name?: string;
	config?: Record<string, any>;
}

export interface PublicTeamLeaderDTO {
	id: number;
	workspaceId: number;
	teamId: number;
	name: string;
	systemPrompt: string;
	personalityConfig: Record<string, any>;
	allowedMcps: string[];
	modelProvider: string;
	modelName: string;
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
}

export interface PublicWorkspaceDTO {
	id: number;
	uid: string;
	ownerId: string;
	name: string;
	description: string | null;
	settings: Record<string, any>;
	brandContext: Record<string, any> | null;
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
	membersCount?: number;
	teamsCount?: number;
	leader?: PublicTeamLeaderDTO;
}

export interface CreateWorkspacePayload {
	name: string;
	description?: string;
	settings?: Record<string, any>;
}

export interface CreateWorkspaceResult {
	workspace: PublicWorkspaceDTO;
	leader?: PublicTeamLeaderDTO | null;
}

export interface PublicTeamDTO {
	id: number;
	uid: string;
	workspaceId: number;
	name: string;
	description: string | null;
	isActive: boolean;
	membersCount?: number;
	createdAt: string;
	updatedAt: string;
}

export interface PublicTeamMemberDTO {
	id: number;
	teamId: number;
	workspaceId: number | null;
	mcpId: number;
	name: string;
	seniority: SeniorityLevel;
	seniorityRecommendedByLeader: boolean;
	customSystemPrompt: string | null;
	customCompetencies: string[] | null;
	customAllowedMcps: string[] | null;
	ferramentaId: number | null;
	status: "disponivel" | "executando" | "descansando" | "erro";
	personalidade: Record<string, any> | null;
	capacidadeUsada: number;
	temperature: number | null;
	maxTokens: number | null;
	modelProvider: string;
	modelName: string;
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
	mcp?: PublicMcpDTO;
	contractedMcp?: PublicContractedMcpDTO;
}

// --- Service ---

export class WorkspaceServices {
	private static readonly BASE_ROUTE = "v1/workspaces";

	// ── Workspaces ────────────────────────────────────────────────

	/** Lista todos os workspaces do usuário autenticado. */
	static async listWorkspaces(): Promise<PublicWorkspaceDTO[]> {
		return APIClientServices.get<PublicWorkspaceDTO[]>(this.BASE_ROUTE);
	}

	/** Obtém detalhes de um workspace pelo UID. */
	static async getWorkspace(uid: string): Promise<PublicWorkspaceDTO> {
		return APIClientServices.get<PublicWorkspaceDTO>(`${this.BASE_ROUTE}/${uid}`);
	}

	/** Cria um novo workspace para o usuário autenticado. */
	static async createWorkspace(data: CreateWorkspacePayload): Promise<CreateWorkspaceResult> {
		return APIClientServices.post<CreateWorkspaceResult>(this.BASE_ROUTE, data);
	}

	// ── Teams ─────────────────────────────────────────────────────

	/** Lista teams de um workspace. */
	static async listTeams(workspaceUid: string): Promise<PublicTeamDTO[]> {
		return APIClientServices.get<PublicTeamDTO[]>(`${this.BASE_ROUTE}/${workspaceUid}/teams`);
	}

	/** Obtém detalhes de um team. */
	static async getTeam(workspaceUid: string, teamUid: string): Promise<PublicTeamDTO> {
		return APIClientServices.get<PublicTeamDTO>(`${this.BASE_ROUTE}/${workspaceUid}/teams/${teamUid}`);
	}

	/** Cria um novo team no workspace. */
	static async createTeam(workspaceUid: string, data: { name: string; description?: string }): Promise<PublicTeamDTO> {
		return APIClientServices.post<PublicTeamDTO>(`${this.BASE_ROUTE}/${workspaceUid}/teams`, data);
	}

	/** Atualiza um team. */
	static async updateTeam(workspaceUid: string, teamUid: string, data: { name?: string; description?: string }): Promise<PublicTeamDTO> {
		return APIClientServices.put<PublicTeamDTO>(`${this.BASE_ROUTE}/${workspaceUid}/teams/${teamUid}`, data);
	}

	/** Remove um team. */
	static async deleteTeam(workspaceUid: string, teamUid: string): Promise<void> {
		await APIClientServices.delete(`${this.BASE_ROUTE}/${workspaceUid}/teams/${teamUid}`);
	}

	// ── Membros (por team) ────────────────────────────────────────

	/** Lista membros de um team. */
	static async getMembers(workspaceUid: string, teamUid?: string): Promise<PublicTeamMemberDTO[]> {
		const path = teamUid ? `${this.BASE_ROUTE}/${workspaceUid}/teams/${teamUid}/members` : `${this.BASE_ROUTE}/${workspaceUid}/members`;
		return APIClientServices.get<PublicTeamMemberDTO[]>(path);
	}

	/** Obtém detalhes de um membro específico. */
	static async getMember(workspaceUid: string, memberId: number, teamUid?: string): Promise<PublicTeamMemberDTO> {
		const path = teamUid
			? `${this.BASE_ROUTE}/${workspaceUid}/teams/${teamUid}/members/${memberId}`
			: `${this.BASE_ROUTE}/${workspaceUid}/members/${memberId}`;
		return APIClientServices.get<PublicTeamMemberDTO>(path);
	}

	/** Contrata um novo membro no team. */
	static async hireMember(workspaceUid: string, data: HireMemberPayload, teamUid?: string): Promise<PublicTeamMemberDTO> {
		const path = teamUid ? `${this.BASE_ROUTE}/${workspaceUid}/teams/${teamUid}/members` : `${this.BASE_ROUTE}/${workspaceUid}/members`;
		return APIClientServices.post<PublicTeamMemberDTO>(path, data);
	}

	/** Atualiza configurações de um membro. */
	static async updateMember(workspaceUid: string, memberId: number, data: UpdateMemberPayload, teamUid?: string): Promise<PublicTeamMemberDTO> {
		const path = teamUid
			? `${this.BASE_ROUTE}/${workspaceUid}/teams/${teamUid}/members/${memberId}`
			: `${this.BASE_ROUTE}/${workspaceUid}/members/${memberId}`;
		return APIClientServices.put<PublicTeamMemberDTO>(path, data);
	}

	/** Remove (desliga) um membro do team. */
	static async removeMember(workspaceUid: string, memberId: number, teamUid?: string): Promise<void> {
		const path = teamUid
			? `${this.BASE_ROUTE}/${workspaceUid}/teams/${teamUid}/members/${memberId}`
			: `${this.BASE_ROUTE}/${workspaceUid}/members/${memberId}`;
		await APIClientServices.delete(path);
	}
}
